import { useEffect, useState, useRef, useCallback } from 'react'
// import * as Sentry from '@sentry/react'
import { App, Skeleton } from 'antd'
import { useAppSelector } from '@/hooks/useReduxHooks'
// import { VkAuthProvider } from '@/context/auth/VkAuthContext'
import { TYPES, DEFAULT_TIMER_EXPIRED_CALLBACK, EXPIRED_ANSWER_TITLE } from '@/constants/quiz'
import { USER_API } from '@/constants/urls'
import { IAnswer, ISurvey } from '@/models'
import { useHttp } from '@/hooks/useHttp'
import { Text, Button } from '@/components/ui'
import { Progress } from './ui'
import { QuizCompleted, QuizQuestions } from './index'
import quizThanksImg from '@/assets/images/quiz/quiz-thanks.svg'
import { useTimer } from '@/hooks/useTimer'
import s from './quiz.module.scss'

interface IQuizLayoutProps {
  activeSurvey: ISurvey | undefined
  setStartSurvey: (e: string) => void
  isWrongSurvey: boolean
}

const QuizLayout = ({ activeSurvey, setStartSurvey, isWrongSurvey }: IQuizLayoutProps) => {
  const [survey, setSurvey] = useState<ISurvey>()
  const [questions, setQuestions] = useState<any>([])
  const [cycleStopped, setCycleStopped] = useState<boolean>(false)
  const callbackRef = useRef(DEFAULT_TIMER_EXPIRED_CALLBACK)
  const [isTimerModalOpen, setIsTimerModalOpen] = useState(false)
  const [madRangeQuestions, setMadRangeQuestions] = useState<null | any[]>(null)
  const token = useAppSelector((state) => state.auth.accessToken)
  const { request, requestErrors, loading } = useHttp()
  const quizContainerRef = useRef<HTMLDivElement>(null)
  const user = useAppSelector((state) => state.user.data)
  const { message } = App.useApp()

  const getQuestion = useCallback(
    async (questionId: number) => {
      try {
        const response = await request<any>(
          `${USER_API}/survey/questions/${questionId}?progress_uuid=${survey?.uuid}`,
          'GET',
          null,
          { Authorization: `Bearer ${token}` }
        )

        if (response && 'data' in response) {
          if (response.data.type === TYPES.MAD_RANGE_IMAGE || response.data.type === TYPES.MAD_RANGE_TEXT) {
            if (madRangeQuestions?.length) {
              setMadRangeQuestions((prev: any) => [...prev, { ...response.data }])
            } else {
              setMadRangeQuestions([{ ...response.data }])
            }
          }
        }

        return response && 'data' in response ? response.data : []
      } catch {
        // Sentry.captureException(e)
        // Sentry.captureMessage('QuizLayout getQuestion USER_API/survey/questions/questionId?progress_uuid=survey.uuid GET')
        message.error('При получении вопроса произошла ошибка')
      }
    },
    [madRangeQuestions?.length, message, request, survey?.uuid, token]
  )

  const getTextoGraphic = useCallback(
    async (questionId: number) => {
      try {
        const response = await request<any>(`${USER_API}/survey/textographic-list/${questionId}`, 'GET', null, {
          Authorization: `Bearer ${token}`
        })
        return response?.data || []
      } catch (e) {
        // Sentry.captureException(e)
        // Sentry.captureMessage('QuizLayout getTextoGraphic USER_API/survey/textographic-list/questionId GET')
        message.error('Произошла ошибка на сервере')
      }
    },
    [message, request, token]
  )

  const setCurrentQuestions = useCallback(
    async (progressQuestionIds: number[]) => {
      const questionsData = []
      for (const progressQuizPage of progressQuestionIds) {
        let question
        if (progressQuizPage > 1000000) {
          question = await getTextoGraphic(progressQuizPage)
        } else {
          question = await getQuestion(progressQuizPage)
        }
        if (question) {
          questionsData.push(question)
        } else {
          return
        }
      }

      setQuestions(questionsData)
    },
    [getQuestion, getTextoGraphic]
  )

  const updateMadRangeQuestion = (question: any) => {
    if (!madRangeQuestions?.length) return

    return setMadRangeQuestions((prev: any) =>
      prev.map((v: any) => {
        if (v?.id === question.id) return question
        return v
      })
    )
  }

  const onSendQuestion = async (data: any, questionType: string, lastQuestionOnPage = false, timerExpired = false) => {
    if (survey) {
      try {
        const finalData = {
          progress_uuid: survey.uuid,
          ...data
        }

        const currentQuestion = questions?.find(({ id }) => id === data.question_id)
        if (survey.tag_prefix && currentQuestion?.tag_prefix) {
          try {
            if (Array.isArray(data.answers_uuid)) {
              data.answers_uuid.forEach((item) => {
                const answer = currentQuestion?.answers.find(({ uuid }) => uuid === item)

                if (answer.tag_prefix) {
                  window.adcm?.call?.({
                    tags: [`${survey.tag_prefix}_${currentQuestion?.tag_prefix}_${answer.tag_prefix}`],
                    profileId: user?.uuid ?? ''
                  })
                }
              })
            } else {
              const answer = currentQuestion?.answers.find(({ uuid }) => uuid === data.answer_uuid)

              if (answer.tag_prefix) {
                window.adcm?.call?.({
                  tags: [`${survey.tag_prefix}_${currentQuestion?.tag_prefix}_${answer.tag_prefix}`],
                  profileId: user?.uuid ?? ''
                })
              }
            }
          } catch (e) {
            // Sentry.captureException(e)
            // Sentry.captureMessage('QuizLayout onSendQuestion')

            console.log('Tag didn`t send', e)
          }
        }

        const response = await request<ISurvey>(
          `${USER_API}/survey/answer/${data.question_id > 1000000 ? 'textographic-list' : questionType}`,
          'POST',
          finalData,
          { Authorization: `Bearer ${token}` }
        )

        if (response?.data && lastQuestionOnPage) {
          const { data } = response
          const setUpData = () => {
            setSurvey(data)
            quizContainerRef?.current?.scrollTo({
              top: 0,
              behavior: 'smooth'
            })
            setQuestions([])

            if (!data.is_ended) setCurrentQuestions(response.data?.progress_quiz_page || [data.progress_question_id])
          }
          if (timerExpired) {
            callbackRef.current = setUpData
          } else {
            setUpData()
          }
        }
      } catch (e) {
        // Sentry.captureException(e)
        // Sentry.captureMessage(
        //   'QuizLayout onSendQuestion USER_API/survey/answer/data.question_id > 1000000 ? textographic-list : questionType POST'
        // )
        if (e.status === 406) {
          return setCycleStopped(true)
        }
        message.error('Произошла ошибка на сервере')
      }
    }
  }

  const [timer, startTimer, resetTimer] = useTimer({
    initialTime: questions[0]?.time_limit ?? 0,
    onFinish: () => {
      setIsTimerModalOpen(true)
      const answerUuid = questions[0].answers.find((v: IAnswer) => v.title === EXPIRED_ANSWER_TITLE)?.uuid
      const finalData = {
        question_id: questions[0].id,
        type: questions[0].type,
        is_cant_answer: false,
        is_timed_out: true,
        // Написать хэлпер на разные типы вопросов
        answer_uuid: questions[0].type === TYPES.ONE_OF_MANY ? answerUuid : undefined,
        answers_uuid: questions[0].type === TYPES.ONE_OF_MANY ? undefined : [answerUuid],
        free_form_answer: questions[0].type === TYPES.ONE_OF_MANY ? '' : []
      }
      onSendQuestion(finalData, finalData.type, true, true)
    }
  })

  useEffect(() => {
    if (!survey && activeSurvey) setSurvey(activeSurvey)
  }, [activeSurvey, survey])

  useEffect(() => {
    if (questions.length && questions[0].time_limit) {
      startTimer()
    }
  }, [questions, startTimer])

  useEffect(() => {
    document?.getElementById('container')?.scroll({ top: 0, behavior: 'smooth' })

    const fetchData = async () => {
      if (questions?.length === 0 && survey && survey?.progress_question_id === activeSurvey?.progress_question_id) {
        await setCurrentQuestions(survey?.progress_quiz_page || [survey.progress_question_id])
      }
    }

    fetchData()
  }, [activeSurvey?.progress_question_id, questions, setCurrentQuestions, survey])

  if (requestErrors) return null

  if (loading) return <Skeleton active />

  if (!survey || survey?.is_ended || cycleStopped) {
    return (
      // <VkAuthProvider>
      <QuizCompleted
        image={quizThanksImg}
        survey={survey}
        setSurvey={(e: string) => setStartSurvey(e)}
        text='Опрос завершен! Благодарим за прохождение опроса!'
        isWrongSurvey={isWrongSurvey}
      />
      // </VkAuthProvider>
    )
  }

  return (
    <div className={s.quiz}>
      <header className={s['quiz-header']}>
        <Text as='p' className={s.title}>
          Опрос
        </Text>

        {!activeSurvey?.hide_progress_bar && (
          <Progress finished={(survey.total_passed / survey.total_questions) * 100} />
        )}
      </header>
      <div ref={quizContainerRef} className={s['quiz-content']}>
        {!activeSurvey?.hide_page_numbers && (
          <Text as='p' size='sm' color='gray-dark' weight='regular' className={s['question-counter']}>
            {`Вопрос ${survey.total_passed + 1} из ${survey.total_questions}`}
          </Text>
        )}
        <QuizQuestions
          questions={questions}
          onSendQuestion={onSendQuestion}
          updateMadRangeQuestion={updateMadRangeQuestion}
          madRangeQuestions={madRangeQuestions}
          progressUuid={activeSurvey?.uuid}
          resetTimer={resetTimer}
          timerValue={questions[0]?.time_limit ? timer : undefined}
        />
        {isTimerModalOpen && (
          <div className={s['modal-layout']}>
            <div className={s.modal}>
              <Text size='xxl' weight='bold'>
                Время на ответ истекло
              </Text>
              <Button
                color='purple-gradient'
                onClick={() => {
                  setIsTimerModalOpen(false)
                  callbackRef.current()
                  callbackRef.current = DEFAULT_TIMER_EXPIRED_CALLBACK
                }}
              >
                Перейти к следующему вопросу
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default QuizLayout
